/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  featureCode: string;
  featureName: string;
  moduleCode: string;
  description: string;
  enabling: boolean;
  currentTotalUsd?: number;
  deltaUsd?: number;
  newTotalUsd?: number;
  proratedAmountUsd?: number;
  accessUntilDate?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

export function FeatureToggleConfirmDialog({
  featureCode, featureName, moduleCode, description, enabling,
  currentTotalUsd, deltaUsd, newTotalUsd, proratedAmountUsd,
  accessUntilDate, onConfirm, onCancel, isLoading,
}: Props) {
  const { t } = useTranslation('platform');

  const title = enabling
    ? t('featureGate.enableTitle', { featureName })
    : t('featureGate.disableTitle', { featureName });

  return (
    <div className="ftcd-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="ftcd-dialog">
        <h3 className="ftcd-title">{title}</h3>
        <p className="ftcd-desc">{description}</p>

        {enabling && currentTotalUsd !== undefined && (
          <div className="ftcd-billing">
            <div className="ftcd-billing-row">
              <span>{t('featureGate.currentBill')}</span>
              <span>{t('featureGate.currencyAmount', { amount: currentTotalUsd.toFixed(2) })}</span>
            </div>
            <div className="ftcd-billing-row ftcd-delta">
              <span>{t('featureGate.addOn', { featureName })}</span>
              <span>{t('featureGate.deltaPerMonth', { amount: deltaUsd?.toFixed(2) })}</span>
            </div>
            <div className="ftcd-billing-row ftcd-total">
              <span>{t('featureGate.newTotal')}</span>
              <span>{t('featureGate.currencyAmount', { amount: newTotalUsd?.toFixed(2) })}</span>
            </div>
            {proratedAmountUsd !== undefined && (
              <p className="ftcd-prorate-note">
                {t('featureGate.proratedNote', { amount: proratedAmountUsd.toFixed(2) })}
              </p>
            )}
          </div>
        )}

        {!enabling && (
          <p className="ftcd-disable-note">
            {accessUntilDate
              ? t('featureGate.disableNote', { date: accessUntilDate })
              : t('featureGate.disableNoteNoDate')}
          </p>
        )}

        <div className="ftcd-actions">
          <button className="ftcd-btn-confirm" onClick={onConfirm} disabled={isLoading}>
            {enabling
              ? t('featureGate.confirmEnable', { price: deltaUsd?.toFixed(2) })
              : t('featureGate.confirmDisable', { featureName })}
          </button>
          <button className="ftcd-btn-cancel" onClick={onCancel} disabled={isLoading}>
            {t('featureGate.cancel')}
          </button>
        </div>
      </div>

      <style>{`
        .ftcd-overlay {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          animation: ftcd-in 0.15s ease both;
        }
        .ftcd-dialog {
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border-subtle);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-xl);
          padding: 1.5rem;
          width: 100%; max-width: 420px;
          animation: ftcd-slide-in 0.2s cubic-bezier(0.22,1,0.36,1) both;
        }
        .ftcd-title {
          margin: 0 0 0.5rem;
          font-family: var(--font-heading);
          font-size: 1.1rem;
          color: var(--color-text-primary);
        }
        .ftcd-desc {
          margin: 0 0 1.25rem;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }
        .ftcd-billing {
          border: 1px solid var(--color-border-subtle);
          border-radius: var(--radius-md);
          overflow: hidden;
          margin-bottom: 1rem;
        }
        .ftcd-billing-row {
          display: flex; justify-content: space-between;
          padding: 0.625rem 0.875rem;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          border-bottom: 1px solid var(--color-border-subtle);
        }
        .ftcd-billing-row:last-child { border-bottom: none; }
        .ftcd-delta { color: var(--color-success); }
        .ftcd-total { font-weight: 600; color: var(--color-text-primary); background: var(--color-bg-tertiary); }
        .ftcd-prorate-note {
          margin: 0.5rem 0 0;
          font-size: 0.775rem;
          color: var(--color-text-muted);
        }
        .ftcd-disable-note {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          margin-bottom: 1rem;
        }
        .ftcd-actions { display: flex; gap: 0.75rem; margin-top: 1.25rem; }
        .ftcd-btn-confirm {
          flex: 1;
          background: var(--color-brand-primary);
          color: #fff;
          border: none; border-radius: var(--radius-md);
          padding: 0.625rem 1rem;
          font-weight: 600; font-size: 0.875rem;
          cursor: pointer; transition: background 0.15s;
        }
        .ftcd-btn-confirm:hover:not(:disabled) { background: var(--color-brand-primary-hover); }
        .ftcd-btn-confirm:disabled { opacity: 0.6; cursor: not-allowed; }
        .ftcd-btn-cancel {
          background: transparent;
          color: var(--color-text-secondary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-md);
          padding: 0.625rem 1rem;
          font-size: 0.875rem; cursor: pointer;
        }
        @keyframes ftcd-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ftcd-slide-in {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ftcd-overlay, .ftcd-dialog { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
